import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Input,
  Select,
  Button,
  Table,
  Tabs,
  Radio,
  Tag,
  Badge,
  Avatar,
  Popconfirm,
  Statistic,
  Space,
  Empty,
  Spin,
  message,
} from "antd";
import SchoolApi from "./api";

const ACCENT = "#9e0c48";
const ACCENT_TINT = "#f9eaf0";
const PAGE_SIZE = 20;

const fullName = (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || "(isimsiz)";
const initials = (r) => `${(r.first_name || "?")[0]}${(r.last_name || "")[0] || ""}`.toUpperCase();

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const SchoolOverview = () => {
  const navigate = useNavigate();

  const [schools, setSchools] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [schoolQuery, setSchoolQuery] = useState("");
  const [schoolId, setSchoolId] = useState(null);

  const [grades, setGrades] = useState([]);
  const [branches, setBranches] = useState([]);
  const [gradeId, setGradeId] = useState("all");
  const [branchId, setBranchId] = useState("all");

  const [academicYear, setAcademicYear] = useState("");
  const [years, setYears] = useState([]);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const reqId = useRef(0); // geç gelen eski cevaplar yeni sonucun üstüne yazmasın

  /* Okullar */
  useEffect(() => {
    setSchoolsLoading(true);
    SchoolApi.listSchools()
      .then((d) => {
        const list = (d || []).filter((s) => s.status !== 3);
        setSchools(list);
        if (list.length) setSchoolId((cur) => cur ?? list[0].id);
      })
      .catch(() => message.error("Okullar getirilemedi"))
      .finally(() => setSchoolsLoading(false));
  }, []);

  /* Seçili okulun seviye + şube ağacı */
  const loadTree = (sid) =>
    Promise.all([SchoolApi.listGrades(sid), SchoolApi.listAllBranches(sid)])
      .then(([gr, br]) => {
        setGrades(gr || []);
        setBranches(br || []);
      })
      .catch(() => message.error("Seviye / şube listesi getirilemedi"));

  useEffect(() => {
    if (!schoolId) return;
    setGradeId("all");
    setBranchId("all");
    setAcademicYear("");
    setSearch("");
    setQ("");
    setPage(1);
    setGrades([]);
    setBranches([]);
    loadTree(schoolId);
  }, [schoolId]);

  /* Arama kutusu: yazmayı bırakınca sorgula */
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const filterParams = () => ({
    grade_id: gradeId !== "all" ? gradeId : undefined,
    branch_id: branchId !== "all" ? branchId : undefined,
    academic_year: academicYear || undefined,
    q: q || undefined,
  });

  const loadStudents = () => {
    if (!schoolId) return;
    const id = ++reqId.current;
    setLoading(true);
    SchoolApi.listSchoolStudents(schoolId, { ...filterParams(), page, page_size: PAGE_SIZE })
      .then((d) => {
        if (id !== reqId.current) return;
        setRows(d?.rows || []);
        setTotal(d?.total || 0);
        setYears(d?.academic_years || []);
      })
      .catch((e) => id === reqId.current && message.error(e?.response?.data?.message || "Öğrenciler getirilemedi"))
      .finally(() => id === reqId.current && setLoading(false));
  };

  useEffect(loadStudents, [schoolId, gradeId, branchId, academicYear, q, page]);

  /* Sayımlar (şube listesindeki student_count: aktif atamalar, tüm yıllar) */
  const branchesByGrade = useMemo(() => {
    const map = {};
    branches.forEach((b) => (map[b.grade_id] = map[b.grade_id] || []).push(b));
    return map;
  }, [branches]);
  const gradeCount = (gid) => (branchesByGrade[gid] || []).reduce((a, b) => a + (b.student_count || 0), 0);
  const schoolTotal = branches.reduce((a, b) => a + (b.student_count || 0), 0);

  const school = schools.find((s) => s.id === schoolId);
  const selectedGrade = grades.find((g) => g.id === gradeId);
  const selectedBranch = branches.find((b) => b.branch_id === branchId);

  const visibleSchools = schools.filter((s) => {
    const t = schoolQuery.trim().toLocaleLowerCase("tr");
    return !t || s.name.toLocaleLowerCase("tr").includes(t) || (s.code || "").toLocaleLowerCase("tr").includes(t);
  });

  const unassign = (r) =>
    SchoolApi.unassignStudent(r.assignment_id)
      .then(() => {
        message.success("Öğrenci şubeden çıkarıldı");
        loadStudents();
        loadTree(schoolId);
      })
      .catch((e) => message.error(e?.response?.data?.message || "İşlem başarısız"));

  const exportCsv = async () => {
    setExporting(true);
    try {
      let all = [];
      for (let p = 1; ; p++) {
        const d = await SchoolApi.listSchoolStudents(schoolId, { ...filterParams(), page: p, page_size: 100 });
        all = all.concat(d?.rows || []);
        if (!d?.rows?.length || all.length >= (d.total || 0)) break;
      }
      const header = ["Öğrenci ID", "Ad", "Soyad", "E-posta", "Telefon", "Seviye", "Şube", "Akademik Yıl"];
      const lines = all.map((r) =>
        [r.student_id, r.first_name, r.last_name, r.email, r.phone, r.grade_name, r.branch_name, r.academic_year]
          .map(csvCell)
          .join(";")
      );
      const blob = new Blob(["﻿" + [header.map(csvCell).join(";"), ...lines].join("\r\n")], {
        type: "text/csv;charset=utf-8",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(school?.code || school?.name || "okul").replace(/\s+/g, "_")}_ogrenciler.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      message.error("Dışa aktarma başarısız");
    } finally {
      setExporting(false);
    }
  };

  const scopeTitle = selectedGrade
    ? `${selectedGrade.name} · ${selectedBranch ? `${selectedBranch.branch_name} şubesi` : "tüm şubeler"}`
    : "Tüm seviyeler";

  const columns = [
    { title: "#", dataIndex: "student_id", width: 90 },
    {
      title: "Ad Soyad",
      render: (_, r) => (
        <Space>
          <Avatar size={32} style={{ background: ACCENT_TINT, color: ACCENT, fontWeight: 600, fontSize: 12 }}>
            {initials(r)}
          </Avatar>
          <span style={{ color: "#212529", fontWeight: 500 }}>{fullName(r)}</span>
        </Space>
      ),
    },
    { title: "E-posta", dataIndex: "email" },
    { title: "Telefon", dataIndex: "phone", width: 150, render: (v) => v || "-" },
    {
      title: "Seviye / Şube",
      width: 160,
      render: (_, r) => <Tag color="blue">{`${r.grade_name} / ${r.branch_name}`}</Tag>,
    },
    { title: "Akademik Yıl", dataIndex: "academic_year", width: 120, render: (v) => v || "-" },
    {
      title: "İşlem",
      width: 110,
      align: "right",
      render: (_, r) => (
        <Popconfirm
          title="Öğrenci şubeden çıkarılsın mı?"
          onConfirm={() => unassign(r)}
          okText="Evet"
          cancelText="Hayır"
        >
          <Button size="small" danger>
            Çıkar
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const tabItems = [
    {
      key: "all",
      label: (
        <span>
          Tüm seviyeler <Badge count={schoolTotal} showZero overflowCount={9999} style={badgeStyle(gradeId === "all")} />
        </span>
      ),
    },
    ...grades.map((g) => ({
      key: String(g.id),
      label: (
        <span>
          {g.name}{" "}
          <Badge count={gradeCount(g.id)} showZero overflowCount={9999} style={badgeStyle(gradeId === g.id)} />
        </span>
      ),
    })),
  ];

  return (
    <div className="container-fluid p-3">
      <h1 className="h3 mb-3">
        <strong>Okul Öğrencileri</strong>
      </h1>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
        {/* ------------------------------ Okullar ------------------------------ */}
        <Card
          size="small"
          title={`Okullar (${schools.length})`}
          style={{ flex: "1 1 280px", maxWidth: "100%" }}
          bodyStyle={{ padding: 8 }}
        >
          <Input.Search
            allowClear
            placeholder="Okul adı veya kodu"
            value={schoolQuery}
            onChange={(e) => setSchoolQuery(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <Spin spinning={schoolsLoading}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: "calc(100vh - 280px)", overflowY: "auto" }}>
              {visibleSchools.map((s) => {
                const sel = s.id === schoolId;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSchoolId(s.id)}
                    style={{
                      textAlign: "left",
                      padding: 12,
                      borderRadius: 6,
                      border: `1px solid ${sel ? ACCENT : "#e9ecef"}`,
                      background: sel ? ACCENT_TINT : "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ fontWeight: 600, color: sel ? ACCENT : "#212529" }}>{s.name}</span>
                      {s.code && <Tag style={{ marginRight: 0 }}>{s.code}</Tag>}
                    </div>
                    <div style={{ fontSize: 13, color: "#6c757d", marginTop: 4 }}>
                      {s.student_count ?? 0} öğrenci · {s.grade_count ?? 0} seviye · {s.branch_count ?? 0} şube
                    </div>
                  </button>
                );
              })}
              {!schoolsLoading && !visibleSchools.length && (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Okul bulunamadı" />
              )}
            </div>
          </Spin>
        </Card>

        {/* --------------------------- Okul detayı --------------------------- */}
        <div style={{ flex: "999 1 560px", minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
          {!school ? (
            <Card>
              <Empty description="Devam etmek için bir okul seçin" />
            </Card>
          ) : (
            <>
              <Card>
                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: "#212529" }}>{school.name}</h2>
                    {school.code && <span style={{ color: "#6c757d" }}>Okul kodu {school.code}</span>}
                  </div>
                  <Space size={32} wrap align="end">
                    <Statistic title="Öğrenci" value={schoolTotal} />
                    <Statistic title="Seviye" value={grades.length} />
                    <Statistic title="Şube" value={branches.length} />
                    <div>
                      <div style={{ color: "rgba(0,0,0,.45)", marginBottom: 4 }}>Akademik yıl</div>
                      <Select
                        style={{ width: 150 }}
                        value={academicYear}
                        onChange={(v) => {
                          setAcademicYear(v);
                          setPage(1);
                        }}
                        options={[{ value: "", label: "Tüm yıllar" }, ...years.map((y) => ({ value: y, label: y }))]}
                      />
                    </div>
                  </Space>
                </div>
              </Card>

              <Card bodyStyle={{ padding: "0 16px 14px" }}>
                <Tabs
                  activeKey={String(gradeId)}
                  onChange={(k) => {
                    setGradeId(k === "all" ? "all" : Number(k));
                    setBranchId("all");
                    setPage(1);
                  }}
                  items={tabItems}
                />
                <Space wrap>
                  <span style={{ fontWeight: 500 }}>Şube:</span>
                  {selectedGrade ? (
                    <Radio.Group
                      buttonStyle="solid"
                      value={branchId}
                      onChange={(e) => {
                        setBranchId(e.target.value);
                        setPage(1);
                      }}
                    >
                      <Radio.Button value="all">Tüm şubeler ({gradeCount(selectedGrade.id)})</Radio.Button>
                      {(branchesByGrade[selectedGrade.id] || []).map((b) => (
                        <Radio.Button key={b.branch_id} value={b.branch_id}>
                          {b.branch_name} ({b.student_count})
                        </Radio.Button>
                      ))}
                    </Radio.Group>
                  ) : (
                    <span style={{ color: "#6c757d" }}>Şubeye göre filtrelemek için önce bir seviye seçin</span>
                  )}
                </Space>
              </Card>

              <Card
                title={
                  <div>
                    <div>{scopeTitle}</div>
                    <div style={{ fontSize: 13, fontWeight: 400, color: "#6c757d" }}>
                      {total} öğrenci · {academicYear || "tüm yıllar"}
                    </div>
                  </div>
                }
                extra={
                  <Space wrap>
                    <Input.Search
                      allowClear
                      placeholder="Ad, soyad, e-posta, telefon"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ width: 260 }}
                    />
                    <Button onClick={exportCsv} loading={exporting} disabled={!total}>
                      Excel'e aktar
                    </Button>
                    <Button type="primary" onClick={() => navigate("/school/students")}>
                      Öğrenci ata
                    </Button>
                  </Space>
                }
              >
                <Table
                  rowKey="assignment_id"
                  size="small"
                  loading={loading}
                  dataSource={rows}
                  columns={columns}
                  scroll={{ x: 900 }}
                  locale={{
                    emptyText: (
                      <Empty
                        description={q ? `"${q}" ile eşleşen öğrenci bulunamadı` : "Bu seçimde henüz öğrenci yok"}
                      />
                    ),
                  }}
                  pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total,
                    showSizeChanger: false,
                    showTotal: (t, [a, b]) => `${a}–${b} / ${t} öğrenci`,
                    onChange: setPage,
                  }}
                />
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const badgeStyle = (active) => ({
  background: active ? "#1890ff" : "#f1f3f5",
  color: active ? "#fff" : "#495057",
  boxShadow: "none",
});

export default SchoolOverview;
